'use strict';

import Drawer from 'bootstrap/js/src/offcanvas';
import VZSpinner from 'Components/VZSpinner';
import { appendCsrfToUrl, getParamFromURL, removeParamFromURL } from 'Util/urlUtils';
import { showErrorModal } from 'Root/modal';
import { appendParamToURL } from 'Root/util/urlUtils';
import { loadCSS } from 'Util/util';

// Expose Offcanvas on window.bs for drawer/modal focus management
window.bs = window.bs || {};
window.bs.Offcanvas = Drawer;

const spinner = document.querySelector('vz-spinner') || new VZSpinner();

/**
 * @class VZOpenDrawer
 * @description Specifically used for the more information drawer
 * @example
 * <vz-open-drawer data-drawer-url="${URLUtils.url('Drawer-Show', 'titleKey', titleResourceKey, 'descriptionKey', descriptionResourceKey, 'bundle', resourceBundle)}">
 *      <AnyHTMLElement/>
 * </vz-open-drawer>
 */
export default class VZOpenDrawer extends HTMLElement {
    constructor() {
        super();

        this.endpoint = this.dataset.drawerUrl;
        this.onClick = this.onClick.bind(this);
        this.handleSuccessResponse = this.handleSuccessResponse.bind(this);
    }

    connectedCallback() {
        this.isMinicartButton = this.classList.contains('js-minicart-button');
        this.isEditMode = document.querySelector('.js-edit-mode');
        this.addEventListener('click', this.onClick);
        this.showMinicartDrawerOnLoad();
    }

    disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
    }

    /**
     * @function showDrawer
     * @description creates a Boostrap offcanvas drawer and shows it
     * @param {HTMLElement} drawerElement the element from which to create a Boostrap Offcanvas element (A.K.A Drawer)
     * @param {HTMLElement} [triggerElement] the element that triggered the drawer opening, used for focus restoration on close
     */
    static showDrawer(drawerElement, triggerElement = null) {
        const bsDrawer = new Drawer(drawerElement, {
            // Disable bootstrap backdrop and enable scroll for minicart drawer, as we'll add custom alternatives
            backdrop: !drawerElement.classList.contains('minicart-drawer'),
            scroll: drawerElement.classList.contains('minicart-drawer'),
            keyboard: true,
            focus: true
        });

        // Store instance for later access
        // eslint-disable-next-line no-underscore-dangle
        drawerElement._bsOffcanvasInstance = bsDrawer;
        // Store trigger element for focus restoration on close
        // eslint-disable-next-line no-underscore-dangle
        drawerElement._triggerElement = triggerElement;
        bsDrawer.show();

        // BO-3270: Add manual focus trap as a fallback
        this.initFocusTrap(drawerElement);
    }

    /**
     * @function showMinicartDrawerOnLoad
     * @description Reopens the minicart after a product is deleted from it and the page reloads.
     * @returns {void} Early exit if the button is not the minicart button.
     */
    showMinicartDrawerOnLoad() {
        if (!this.isMinicartButton) return;
        const showMinicart = getParamFromURL(window.location.href, 'showMinicart') === 'true';

        if (showMinicart) {
            this.dispatchEvent(new CustomEvent('click'));

            // To avoid opening the minicart again and again on page refresh, the param needs to removed
            const urlWithoutParam = removeParamFromURL(window.location.href, 'showMinicart');
            window.history.pushState(null, '', urlWithoutParam); // Update URL without refreshing the page
        }
    }

    /**
     * @function initFocusTrap
     * @description Initializes manual focus trap for drawers to ensure users can't tab outside. Bootstrap focus trap is not working as expected for drawers.
     * @param {HTMLElement} drawerElement the drawer element
     */
    static initFocusTrap(drawerElement) {
        // Get all focusable elements (within the drawer)
        const getFocusableElements = () => {
            const focusableSelectors = [
                'a[href]',
                'button:not([disabled])',
                'textarea:not([disabled])',
                'input:not([disabled])',
                'select:not([disabled])',
                '[tabindex]:not([tabindex="-1"])'
            ].join(',');

            // Filter out any hidden elements
            return Array.from(drawerElement.querySelectorAll(focusableSelectors)).filter((el) => el.offsetParent !== null);
        };

        const handleTabKey = (event) => {
            // If tab key was not pressed, do nothing
            if (event.key !== 'Tab') return;

            // If no focusable elements, do nothing
            const focusableElements = getFocusableElements();
            if (focusableElements.length === 0) return;

            // The tab loop
            const firstFocusable = focusableElements[0];
            const lastFocusable = focusableElements[focusableElements.length - 1];

            // Shift+Tab on first element, go to last focusable element
            if (event.shiftKey && document.activeElement === firstFocusable) {
                event.preventDefault();
                lastFocusable.focus();
            }

            // Tab on last element, go to first focusable element
            else if (!event.shiftKey && document.activeElement === lastFocusable) {
                event.preventDefault();
                firstFocusable.focus();
            }
        };

        // Tab key listener
        drawerElement.addEventListener('keydown', handleTabKey);

        // Focus cleanup
        // eslint-disable-next-line no-underscore-dangle
        drawerElement._cleanupFocusTrap = () => {
            drawerElement.removeEventListener('keydown', handleTabKey);
        };

        // On init, focus the first focusable element
        drawerElement.addEventListener('shown.bs.offcanvas', () => {
            const focusableElements = getFocusableElements();

            if (focusableElements.length > 0) {
                focusableElements[0].focus();
            } else {
                drawerElement.focus();
            }
        });
    }

    /**
     * @function closeDrawer
     * @description closes a Bootstrap offcanvas drawer using the stored instance or fallback methods
     * @param {HTMLElement} drawerElement the drawer element to close
     * @returns {boolean} true if drawer was closed, false if no method worked
     */
    static closeDrawer(drawerElement) {
        if (!drawerElement) return false;

        // Try stored instance first
        // eslint-disable-next-line no-underscore-dangle
        if (drawerElement._bsOffcanvasInstance) {
            // eslint-disable-next-line no-underscore-dangle
            drawerElement._bsOffcanvasInstance.hide();
            return true;
        }

        // Fallback to getInstance method
        const bsOffcanvas = window.bootstrap?.Offcanvas?.getInstance(drawerElement);
        if (bsOffcanvas) {
            bsOffcanvas.hide();
            return true;
        }

        // Last resort: click simulation
        const closeButton = drawerElement.querySelector('[data-bs-dismiss="offcanvas"]');
        if (closeButton) {
            closeButton.click();
            return true;
        }

        return false;
    }

    /**
     * @function addDrawerToDOM
     * @description adds the drawer returned from the endpoint to the DOM
     * @param {JSON} data returned from calling Drawer-Show
     */
    static addDrawerToDOM(data) {
        const body = document.body;
        body.insertAdjacentHTML('beforeend', data.html);
    }

    /**
     * @function handleSuccessResponse
     * @description handles a successful call to Drawer-Show
     * @param {JSON} data returned from calling Drawer-Show
     */
    handleSuccessResponse(data) {
        this.constructor.addDrawerToDOM(data);
        loadCSS(this.getAttribute('loadcss'));
        const drawer = document.getElementById('drawer');
        const drawerElement = drawer.closest('.js-drawer-container');

        if (!drawerElement) return;
        const triggerButton = this.querySelector('.js-open-drawer-trigger') || this;
        this.constructor.showDrawer(drawerElement, triggerButton);
        if (this.isMinicartButton) {
            document.dispatchEvent(new CustomEvent('minicart:open')); // This will open the abandonConfigurationModel triggered inside VZModal.js
        }
    }

    /**
     * @function onClick
     * @description Handles the click event on an open drawer button
     * @param {Event} event - The click event
     */
    async onClick(event) {
        event.preventDefault(); // This is needed so that checkboxes on the configurator are not checked when more information is clicked
        const endpointWithCsrf = appendCsrfToUrl(this.endpoint);
        const isConfiguratorPage = this.isMinicartButton ? this?.dataset.isConfiguratorPage : false;

        // Get and Add the pliId to the querystring to make it available in the minicart drawer only in edit mode.
        const productPliID = this.isMinicartButton && this.isEditMode ? getParamFromURL(window.location.href, 'pliId') : false;
        const editModeEndpoint = productPliID ? appendParamToURL(endpointWithCsrf, 'productPliID', productPliID) : false;
        const finalEndpoint = this.isMinicartButton ? appendParamToURL(editModeEndpoint || endpointWithCsrf, 'isConfiguratorPage', isConfiguratorPage) : endpointWithCsrf;

        spinner.start();
        try {
            const response = await fetch(finalEndpoint);

            if (!response.ok) {
                throw new Error(`There was an error fetching the endpoint - ${this.endpoint} - status: ${response.status}`);
            }

            const data = await response.json();
            this.handleSuccessResponse(data);
        } catch (error) {
            console.warn(error);
            const { title, body, cta } = JSON.parse(this.dataset?.generalError);
            showErrorModal(title, body, cta);
        } finally {
            spinner.stop();
        }
    }
}

if (!window.customElements.get('vz-open-drawer')) {
    window.customElements.define('vz-open-drawer', VZOpenDrawer);
}
